import { z } from 'zod';
import { zId, zMoney } from './primitives.js';

/**
 * A-08…A-11 — catalog authoring. PR-01, CR-45, CR-47, CR-48, CR-49.
 *
 * Defined once and used by the NestJS route, the React form and the tests, so a
 * field cannot mean one thing on each side.
 *
 * Two absences are deliberate and are the product, not an oversight:
 *
 *   - **No quantity, availability, stock or lead time.** Every wholesale order is
 *     made to order (D4). The pressure to add one is highest on exactly this
 *     screen, because every catalog tool elsewhere has it.
 *   - **No minimum order quantity.** Removed by CR-07, including an enforcement
 *     branch that was still live in the order service.
 *
 * If either looks missing, it has been misread.
 */

/** A slug is stable and appears in a URL: lowercase, hyphenated. */
export const zSlug = z.string().min(2).max(64).regex(/^[a-z0-9]+(-[a-z0-9]+)*$/);

/**
 * AUR-RG. Every variant code is built from this plus purity, colour and carat, so
 * the prefix has to survive being concatenated — uppercase and hyphens only.
 */
export const zCodePrefix = z.string().min(2).max(24).regex(/^[A-Z0-9]+(-[A-Z0-9]+)*$/);

/**
 * A gram or carat weight. NUMERIC(19,4) in the database and a STRING on the wire
 * for the same reason money is: `postgres-js` is pinned to return NUMERIC as a
 * string, and parsing it to a float here would lose precision before any of our
 * code ran. Sort and compare these in SQL, not after parsing in JS.
 */
export const zWeight = z
  .string()
  .regex(/^\d{1,12}(\.\d{1,4})?$/, 'must be a decimal weight with at most 4 fractional digits');

export const zCollectionState = z.enum(['DRAFT', 'PUBLISHED']);

// ------------------------------------------------------------------ taxonomy

export const zCreateCollection = z.object({
  name: z.string().min(2).max(80),
  slug: zSlug,
  state: zCollectionState.default('DRAFT'),
  sortOrder: z.coerce.number().int().min(0).max(9999).default(0),
});

export const zUpdateCollection = zCreateCollection.partial().omit({ slug: true });

export const zCreateCategory = z.object({
  name: z.string().min(2).max(80),
  slug: zSlug,
  sortOrder: z.coerce.number().int().min(0).max(9999).default(0),
});

export const zUpdateCategory = zCreateCategory.partial().omit({ slug: true });

// ------------------------------------------------------------------- product

/**
 * CR-48, CR-49 — how the piece is made and what mark it carries. Each points at a
 * row in `mki_attribute_values`, and the value must be ACTIVE for the portal in
 * context. That is checked by the service, not here: a Zod schema cannot know
 * which portal it is being parsed for.
 */
const zProductMake = z.object({
  settingValueId: zId.nullable().optional(),
  finishValueId: zId.nullable().optional(),
  /** Bracelets and chains only; null everywhere else. */
  claspValueId: zId.nullable().optional(),
  /** Earrings only; null everywhere else. */
  backingValueId: zId.nullable().optional(),
  hallmarkValueId: zId.nullable().optional(),
});

export const zCreateProduct = zProductMake.extend({
  name: z.string().min(2).max(120),
  codePrefix: zCodePrefix,
  description: z.string().max(4000).nullable().optional(),
  collectionId: zId.nullable().optional(),
  categoryId: zId.nullable().optional(),
  /** CR-45 — one metal type per product; purity and colour depend on it. */
  metalTypeCode: z.string().min(1).max(24),
  /**
   * CR-47 — a customs declaration needs a tariff code and an origin. Origin stays
   * nullable until Miraki confirms where it manufactures: a guess prints on a
   * customs document as though it were fact.
   */
  hsCode: z.string().max(16).nullable().optional(),
  countryOfOrigin: z.string().length(2).regex(/^[A-Z]{2}$/).nullable().optional(),
  /**
   * CR-45/CR-12 — every value of every axis (Purity, Metal colour, Carat,
   * and Stone's Material/Shape/Cut/Colour/Clarity) this design is offered
   * in, as one flat list of attribute-value ids. Omitted means "leave as
   * is"; an empty array clears it, the same "supplied replaces the whole
   * set" rule `zCreateVariant`'s id fields already live by.
   */
  offeredValueIds: z.array(zId).max(200).optional(),
});

/** `codePrefix` is absent: changing it would orphan every variant code built from it. */
export const zUpdateProduct = zCreateProduct.partial().omit({ codePrefix: true });

// ------------------------------------------------------------------- variant

/**
 * `code` is DERIVED WHEN ABSENT, ACCEPTED WHEN SUPPLIED.
 *
 * Deriving is the default and the reason still holds: a hand-typed code is how
 * two variants end up meaning the same thing, and `UNIQUE (portal_id, code)`
 * catches the rest.
 *
 * But a supplier who already has a SKU has the better name for the piece. Real
 * client data settled this: 731 variants arrived carrying SKUs like
 * `RN-428797-YG-10K-D-VVS1`, unique across every row, encoding axes our schema
 * has no column for — lab-grown versus natural, diamond colour, clarity. Derived
 * from what we DO hold, 622 of those 731 collapse onto a code already used.
 * Deriving a second name for a piece whose owner already has one, and losing 85%
 * of the catalog doing it, is worse than accepting theirs.
 *
 * Supplied codes are still uniqueness-checked. Nothing else about the rules
 * changes.
 */
export const zVariantCode = z.string().min(3).max(64).regex(/^[A-Za-z0-9]+([-_][A-Za-z0-9]+)*$/);

export const zCreateVariant = z.object({
  productId: zId,
  /** Omit to have it derived. Supply it to keep a code the business already uses. */
  code: zVariantCode.optional(),
  purityValueId: zId.nullable().optional(),
  metalColourValueId: zId.nullable().optional(),
  /** Centre-stone carat as a code — '050' resolves to 0.50ct. */
  caratCode: z.string().max(8).nullable().optional(),
  /**
   * Stone — a variant-defining axis, the same standing as purity and metal
   * colour above. `gemstoneValueId` is the MATERIAL (Emerald, Ruby, Sapphire,
   * Diamond — attribute key `gemtype`), kept deliberately separate from
   * `shape`'s own 'EM' (emerald-CUT). The other four point at the same
   * global trade lists `mki_product_stones` already uses for a range
   * (`shape`, `cut`, `scol`, `clar`); here they name one exact point in it.
   */
  /**
   * Lab Grown or Natural (attribute key `origin`) — an ORIGIN, orthogonal to
   * the material beside it. Its own axis rather than four more `gemtype` rows,
   * because folding it in would grow the material list by multiplication.
   * Priced, not descriptive: three of the client's six catalogue files ship
   * both values and the origin changes the price in every one.
   */
  stoneOriginValueId: zId.nullable().optional(),
  gemstoneValueId: zId.nullable().optional(),
  stoneShapeValueId: zId.nullable().optional(),
  stoneCutValueId: zId.nullable().optional(),
  stoneColourValueId: zId.nullable().optional(),
  stoneClarityValueId: zId.nullable().optional(),
  netMetalWeightG: zWeight.nullable().optional(),
  grossWeightG: zWeight.nullable().optional(),
  totalCaratWeight: zWeight.nullable().optional(),
  /** PR-01 — base list price in the portal's base currency. Never a number. */
  listPrice: zMoney,
  /**
   * PR-02 — the RP, the retailer's suggested selling price to their customer.
   *
   * Optional on the wire, not absent from the model: omit it and the write path
   * applies PR-05's default of 3x the wholesale price, the markup the client's
   * note states. Send it to override (D59). PR-03 — the retailer is invoiced on
   * the wholesale price, never on this one.
   */
  rpPrice: zMoney.optional(),
  /** The metafield seam. Not a dumping ground for things that have earned a column. */
  attributes: z.record(z.string(), z.unknown()).default({}),
});

export const zUpdateVariant = zCreateVariant.partial().omit({ productId: true });

// -------------------------------------------------------------- authoring A-09

/**
 * CR-43 — the combinations a definition implies.
 *
 * A PROPOSAL and nothing more: the server answers with candidates and writes
 * NOTHING. The author prunes what is not made and prices what is, then saves
 * through the existing bulk import route. Generation that wrote rows would make
 * "show me what this would be" indistinguishable from "create 252 products".
 *
 * There is deliberately **no price field here**. Generation settles identity;
 * price is set in the grid afterwards, and `zCreateVariant.listPrice` already
 * carries it as a money string.
 *
 * An empty array means "this axis does not apply" — a metal with no colour
 * choice, or a design with no stone — not "every value".
 */
export const zGenerateVariants = z.object({
  productId: zId,
  purityValueIds: z.array(zId).min(1),
  metalColourValueIds: z.array(zId),
  caratCodes: z.array(z.string().max(8)),
  stoneOriginValueIds: z.array(zId),
  gemstoneValueIds: z.array(zId),
  stoneShapeValueIds: z.array(zId),
  stoneCutValueIds: z.array(zId),
  stoneColourValueIds: z.array(zId),
  stoneClarityValueIds: z.array(zId),
});
export type GenerateVariants = z.infer<typeof zGenerateVariants>;

/**
 * CR-43 — a stone the design carries. A piece can hold several and they are
 * rarely alike: a certified centre and twelve uncertified shoulders.
 *
 * The grading fields are a **range** the piece is built to ('0.30 – 1.00',
 * '0.005 each'), which is why they are free text rather than attribute ids. The
 * exact four Cs of one sellable piece live on the variant.
 */
export const zProductStone = z.object({
  /** Centre, Shoulder, Accent. */
  position: z.string().min(1).max(32),
  shapeValueId: zId.nullable().optional(),
  stoneCount: z.number().int().min(1).max(999),
  caratSpec: z.string().max(64).nullable().optional(),
  cutSpec: z.string().max(64).nullable().optional(),
  colourSpec: z.string().max(64).nullable().optional(),
  claritySpec: z.string().max(64).nullable().optional(),
  isCertified: z.boolean(),
});
export type ProductStone = z.infer<typeof zProductStone>;

/**
 * A certification on a product — CR-43, as the client has since asked for it:
 * which body issued it, which variant it is for, its number, and (uploaded
 * separately, many per certification) the certificate documents themselves.
 * Which bodies Miraki uses is D5.
 *
 * Field names are the ones the table was created with; renaming them would break
 * the previous release during a rollout. Read them by what they now hold:
 *   body        the certification body — GIA or IGI (older rows may say 'Miraki')
 *   appliesWhen the VARIANT CODE the certificate was issued for
 *   scope       the CERTIFICATE NUMBER
 *
 * `id` is optional and is how a certification keeps its attachments across a
 * save: the set is replaced whole, and a row that arrives with the id it already
 * had is the same certification. A new row may carry a client-generated uuid so
 * documents can be attached in the same save; without one the server mints it.
 */
export const zCertificateRule = z.object({
  id: zId.optional(),
  body: z.string().min(1).max(32),
  appliesWhen: z.string().min(1).max(200),
  scope: z.string().min(1).max(200),
});
export type CertificateRule = z.infer<typeof zCertificateRule>;

// ----------------------------------------------------------------- publishing

/**
 * Publishing is a separate action from saving, and it is checked twice: once here
 * for shape, and again in the service at the moment of publishing. An attribute
 * value deactivated after a draft was written has to stop that draft going live,
 * which a save-time check alone would miss.
 */
export const zSetPublished = z.object({ isPublished: z.boolean() });
export const zSetVariantActive = z.object({ isActive: z.boolean() });

export type CreateProduct = z.infer<typeof zCreateProduct>;
export type UpdateProduct = z.infer<typeof zUpdateProduct>;
export type CreateVariant = z.infer<typeof zCreateVariant>;
export type UpdateVariant = z.infer<typeof zUpdateVariant>;
export type CreateCollection = z.infer<typeof zCreateCollection>;
export type CreateCategory = z.infer<typeof zCreateCategory>;

// ------------------------------------------------------------------ bulk load

/**
 * A-11. One request carrying a whole submission, because the row-at-a-time path
 * cannot serve it.
 *
 * Posting each row separately costs a full request each: session, permission,
 * page and portal resolution — about twenty-one queries — before the route does
 * any work. Against a database on another continent that is roughly five seconds
 * a row, and 731 rows took sixty-eight minutes of which almost all was network
 * wait. Resolving the actor ONCE and inserting in one transaction turns that into
 * a handful of round trips.
 *
 * Products and variants travel together: a variant names its product by
 * `productCode`, not by an id the client cannot know for a product created in the
 * same request.
 */
export const zBulkProduct = zCreateProduct.extend({
  /** Named rather than referenced, so a collection can be resolved server-side. */
  collection: z.string().max(80).nullable().optional(),
  category: z.string().max(80).nullable().optional(),
}).omit({ collectionId: true, categoryId: true });

export const zBulkVariant = z.object({
  /** Which product, by its code prefix — the id does not exist yet on a create. */
  productCode: zCodePrefix,
  code: zVariantCode.optional(),
  purity: z.string().max(24).nullable().optional(),
  metalColour: z.string().max(24).nullable().optional(),
  caratCode: z.string().max(8).nullable().optional(),
  /**
   * Stone, as CODES — the same convention as `purity` and `metalColour` above,
   * because a spreadsheet carries 'VVS1' and not a uuid.
   *
   * These were missing, and their absence was a real defect rather than an
   * omission: the bulk path is the only way the product editor creates variants,
   * so without them every variant it made had null stone columns. The retailer
   * screen builds its stone dropdowns from those columns, so the whole priced-
   * stone axis went dark for anything authored through the grid — and the
   * derived code, which also reads them, collapsed 200 Colette rows onto about
   * ten names and collided.
   */
  stoneOrigin: z.string().max(24).nullable().optional(),
  gemstone: z.string().max(24).nullable().optional(),
  stoneShape: z.string().max(24).nullable().optional(),
  stoneCut: z.string().max(24).nullable().optional(),
  stoneColour: z.string().max(24).nullable().optional(),
  stoneClarity: z.string().max(24).nullable().optional(),
  netMetalWeightG: zWeight.nullable().optional(),
  grossWeightG: zWeight.nullable().optional(),
  totalCaratWeight: zWeight.nullable().optional(),
  listPrice: zMoney,
  /** PR-02. An omitted row takes PR-05's 3x default, same as a single write. */
  rpPrice: zMoney.optional(),
  attributes: z.record(z.string(), z.unknown()).default({}),
});

/**
 * `dryRun` runs every check and writes nothing — BU-04, and the half A-11 draws
 * as the validation report. Commit is partial by default (BU-05): valid rows land
 * and refused rows come back, because on a catalog of several hundred variants
 * one bad row rejecting the file makes the feature unusable.
 */
export const zBulkImport = z.object({
  products: z.array(zBulkProduct).max(2000).default([]),
  variants: z.array(zBulkVariant).max(5000).default([]),
  dryRun: z.boolean().default(false),
});

export type BulkImport = z.infer<typeof zBulkImport>;
export type BulkProduct = z.infer<typeof zBulkProduct>;
export type BulkVariant = z.infer<typeof zBulkVariant>;

/**
 * Publish or withdraw several products at once — A-08's bulk action.
 *
 * Cascades to the products' variants, because publishing the product alone shows
 * a retailer nothing: they see a variant only when BOTH the product and the
 * variant are published. An administrator selecting rows and pressing Publish
 * means "offer these", not "set a flag that has no effect on its own".
 */
export const zBulkPublish = z.object({
  ids: z.array(zId).min(1).max(500),
  isPublished: z.boolean(),
});
export type BulkPublish = z.infer<typeof zBulkPublish>;

/**
 * Withdraw several variants at once — a whole group selected in the
 * Variance block, in one request rather than one per row. The write itself
 * is a genuine removal (`CatalogService.deleteVariants`), the same as the
 * single-variant route; this exists so a group of any size costs one round
 * trip and one bulk statement, not N of each.
 */
export const zBulkDeleteVariants = z.object({
  ids: z.array(zId).min(1).max(500),
});
export type BulkDeleteVariants = z.infer<typeof zBulkDeleteVariants>;

/**
 * A-08's Withdraw, on the product list — a genuine removal
 * (`CatalogService.deleteProducts`), the same redefinition Withdraw already
 * got inside the Variance block: `isPublished: false` only ever hid a
 * product, it never answered "get rid of this". One request for the whole
 * selection, the same shape as `zBulkDeleteVariants`.
 */
export const zBulkDeleteProducts = z.object({
  ids: z.array(zId).min(1).max(500),
});
export type BulkDeleteProducts = z.infer<typeof zBulkDeleteProducts>;
